import React, { useState, useEffect } from 'react';
import { trackEvent } from '../../services/analytics';
import SEO from '../SEO';
import { Link, useLocation } from 'react-router-dom';
import {
    ArrowRight, Check, ChevronDown, ChevronUp,
    Star, Quote, BadgeCheck
} from 'lucide-react';
import AuthModal from './AuthModal';
import TrafficTestWidget from './TrafficTestWidget';
import LiveCounter from './LiveCounter';
import ExitIntentPopup from './ExitIntentPopup';
import LazySection from './LazySection';
import { TIERS, PRICING_MATRIX, formatPrice, formatCPM, getCPM, TierId } from '../../constants/pricing';

// ─── Custom Brand SVG Icons ─────────────────────────────────────────────────────
// Matches the website's Lucide-style design language: 24x24 viewBox, 2px stroke,
// round caps/joins, brand color #ff5a0a

const TCIcons = {
    // Section badge icons
    alertTriangle: (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
            <path d="M12 9v4" /><path d="M12 17h.01" />
        </svg>
    ),
    zap: (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
        </svg>
    ),
    sparkles: (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z" />
            <path d="M5 3v4" /><path d="M19 17v4" /><path d="M3 5h4" /><path d="M17 19h4" />
        </svg>
    ),
    shieldCheck: (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10" />
            <path d="m9 12 2 2 4-4" />
        </svg>
    ),
    checkCircle: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
            <polyline points="22 4 12 14.01 9 11.01" />
        </svg>
    ),

    // Problem section – large icons (32px)
    trendingUpBig: (
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="22 7 13.5 15.5 8.5 10.5 2 17" />
            <polyline points="16 7 22 7 22 13" />
        </svg>
    ),
    cursorClick: (
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 3 5 6.99h3V14h2V6.99h3L9 3Z" />
            <path d="m12 12 4 10 1.7-4.3L22 16l-10-4Z" />
        </svg>
    ),
    eyeOff: (
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24" />
            <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68" />
            <path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61" />
            <line x1="2" x2="22" y1="2" y2="22" />
        </svg>
    ),

    // How it works – medium icons (24px)
    globeSearch: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="9" />
            <path d="M11 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10" />
            <path d="M11 2a15.3 15.3 0 0 0-4 10 15.3 15.3 0 0 0 4 10" />
            <path d="M2 11h18" />
            <path d="m21 21-2-2" />
        </svg>
    ),
    signalTower: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4.9 16.1C1 12.2 1 5.8 4.9 1.9" />
            <path d="M7.8 4.7a6.14 6.14 0 0 0-.8 9.6" />
            <circle cx="12" cy="9" r="2" />
            <path d="M16.2 4.7c2.4 2.4 2.7 6.1.8 9.6" />
            <path d="M19.1 1.9a12.98 12.98 0 0 1 0 14.2" />
            <path d="M12 11v6" /><path d="M10 17h4" /><path d="M9 21h6" />
        </svg>
    ),
    monitorChart: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect width="20" height="14" x="2" y="3" rx="2" />
            <path d="M12 17v4" /><path d="M8 21h8" />
            <path d="m6 13 3-3 2 2 5-5" />
            <path d="m16 7 0 4-4 0" />
        </svg>
    ),

    // Guarantee section – medium-large icons (28px)
    shieldGuarantee: (
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10" />
            <path d="m9 12 2 2 4-4" />
        </svg>
    ),
    lockKeyhole: (
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="16" r="1" />
            <rect x="3" y="10" width="18" height="12" rx="2" />
            <path d="M7 10V7a5 5 0 0 1 10 0v3" />
        </svg>
    ),
    receiptCheck: (
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1Z" />
            <path d="m9 10.5 2 2 4-4" />
            <path d="M9 16h6" />
        </svg>
    ),

    // Brand / platform logos for social proof bar
    googleAnalytics: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <rect x="17" y="2" width="4" height="20" rx="2" fill="#F9AB00" />
            <rect x="10" y="8" width="4" height="14" rx="2" fill="#E37400" />
            <circle cx="5" cy="19" r="3" fill="#E37400" />
        </svg>
    ),
    semrush: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" stroke="#FF622D" strokeWidth="2" />
            <path d="M8 16c.5-1.5 1.5-3 4-3s3.5 1.5 4 3" stroke="#FF622D" strokeWidth="2" strokeLinecap="round" />
            <circle cx="9" cy="10" r="1.5" fill="#FF622D" />
            <circle cx="15" cy="10" r="1.5" fill="#FF622D" />
        </svg>
    ),
    wordpress: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" stroke="#21759B" strokeWidth="1.8" />
            <path d="M3.5 12 8 21.5" stroke="#21759B" strokeWidth="1.8" strokeLinecap="round" />
            <path d="M12 2.5c2 3 4 8 3 13" stroke="#21759B" strokeWidth="1.8" strokeLinecap="round" />
            <path d="M20.5 12 16 21.5" stroke="#21759B" strokeWidth="1.8" strokeLinecap="round" />
            <path d="M5 8h6l-2 11" stroke="#21759B" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    ),
    ahrefs: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" stroke="#1A6BFF" strokeWidth="2" />
            <path d="M12 6v12" stroke="#1A6BFF" strokeWidth="2.5" strokeLinecap="round" />
            <path d="M8 10l4-4 4 4" stroke="#1A6BFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M8 18h8" stroke="#1A6BFF" strokeWidth="2" strokeLinecap="round" />
        </svg>
    ),
};

const BrandLogos: Record<string, React.ReactNode> = {
    'Google Analytics': TCIcons.googleAnalytics,
    'Semrush': TCIcons.semrush,
    'WordPress': TCIcons.wordpress,
    'Ahrefs': TCIcons.ahrefs,
};

interface ConversionLandingPageProps {
    onLogin: () => void;
    onNavigate: (page: string) => void;
}

const ConversionLandingPage: React.FC<ConversionLandingPageProps> = ({ onLogin, onNavigate }) => {
    const [scrolled, setScrolled] = useState(false);
    const [authModalOpen, setAuthModalOpen] = useState(false);
    const [authMode, setAuthMode] = useState<'login' | 'signup'>('signup');
    const [authMessage, setAuthMessage] = useState('');
    const [pendingUrl, setPendingUrl] = useState('');
    const [openFaq, setOpenFaq] = useState<number | null>(0);
    const [selectedTier, setSelectedTier] = useState<TierId>('professional');
    const location = useLocation();

    useEffect(() => {
        const handleScroll = () => setScrolled(window.scrollY > 50);
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Dynamic schema based on current route path
    const currentPath = location.pathname;
    const canonicalUrl = `https://traffic-creator.com${currentPath === '/landing2' ? '/free-website-traffic' : currentPath}`;

    const webpageSchema = {
        "@context": "https://schema.org",
        "@type": "WebPage",
        "name": "Free Website Traffic Generator — Get 2,000 Free Visitors | TC",
        "description": "Get free website traffic for testing. Traffic Creator offers 2,000 residential visitor sessions with GA4-visible analytics data. No credit card required.",
        "url": canonicalUrl,
        "mainEntity": {
            "@type": "Service",
            "name": "Free Website Traffic Generator",
            "provider": {
                "@type": "Organization",
                "name": "Traffic Creator"
            },
            "offers": {
                "@type": "Offer",
                "price": "0",
                "priceCurrency": "USD",
                "description": "6,000 free visitors for new users"
            }
        }
    };

    const faqSchema = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": [
            {
                "@type": "Question",
                "name": "Is this traffic safe for my AdSense account?",
                "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Yes, our traffic is designed to be AdSense-safe. We use residential IPs and natural behavior patterns that mimic real visitors."
                }
            },
            {
                "@type": "Question",
                "name": "Are these real visitors or bots?",
                "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "We use high-quality traffic with residential IPs that appears as real visitors in your analytics. Each visit includes realistic behavior patterns."
                }
            },
            {
                "@type": "Question",
                "name": "Will I see the traffic in Google Analytics?",
                "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Absolutely. All traffic is fully compatible with Google Analytics 4, Google Search Console, and other major analytics platforms."
                }
            },
            {
                "@type": "Question",
                "name": "How do I get free website traffic?",
                "acceptedAnswer": {
                    "@type": "Answer",
                    "text": "Simply enter your website URL above, select your target country, and click 'Start Free Traffic'. Create a free account to receive 6,000 visitors at no cost."
                }
            }
        ]
    };

    const handleOpenAuth = (mode: 'login' | 'signup') => {
        setAuthMode(mode);
        setAuthMessage('');
        setAuthModalOpen(true);
    };

    const handleTestComplete = (email: string, url: string) => {
        setPendingUrl(url);
        setAuthMessage('Your campaign is ready! Create a free account to receive 6,000 visitors.');
        setAuthMode('signup');
        setAuthModalOpen(true);
    };

    const handleAuthSuccess = (role: 'user' | 'admin') => {
        setAuthModalOpen(false);
        onLogin();
    };

    const toggleFaq = (index: number) => {
        const isOpening = openFaq !== index;
        setOpenFaq(isOpening ? index : null);
        if (isOpening) {
            trackEvent('faq_click', {
                faq_question: faqs[index]?.question,
                faq_index: index,
                page: location.pathname,
            });
        }
    };

    const faqs = [
        {
            question: "How do I get free website traffic?",
            answer: "Simply enter your website URL above, select your target country, and click 'Start Free Traffic'. Create a free account to receive 6,000 visitors at no cost—no credit card required."
        },
        {
            question: "Is this traffic safe for my AdSense account?",
            answer: "Yes, our traffic is designed to be AdSense-safe. We use residential IPs and natural behavior patterns that mimic real visitors. Our traffic passes all major analytics filters."
        },
        {
            question: "Are these real visitors or bots?",
            answer: "We use high-quality traffic with residential IPs that appears as real visitors in your analytics. Each visit includes realistic behavior patterns: page views, time on site, and navigation patterns."
        },
        {
            question: "Will I see the traffic in Google Analytics?",
            answer: "Absolutely. All traffic is fully compatible with Google Analytics 4, Google Search Console, and other major analytics platforms. You'll see real-time visitors, sessions, and engagement metrics."
        },
        {
            question: "Are there any hidden costs or subscriptions?",
            answer: "No hidden fees, no mandatory subscriptions. Purchase credit packs as needed, or choose monthly plans for agencies. Everything is transparent and clearly priced."
        },
        {
            question: "What if I'm not satisfied?",
            answer: "We offer a full money-back guarantee. If we fail to deliver the promised visitor count within 72 hours, you're eligible for a complete refund—no questions asked."
        },
        {
            question: "Can I target specific countries?",
            answer: "Yes! We offer geo-targeting for 190+ countries. Premium plans include city-level targeting for local SEO campaigns."
        }
    ];

    const pricingPacks = [
        { volume: 70000, name: "Starter", desc: "Perfect for testing" },
        { volume: 600000, name: "Growth", desc: "Most popular choice", highlight: true, badge: "Most Popular" },
        { volume: 1200000, name: "Business", desc: "Serious growth", badge: "Best Value", badgeColor: "bg-green-500" },
    ];

    const problems = [
        { icon: TCIcons.trendingUpBig, title: "No Traffic Baseline?", desc: "It is hard to evaluate pages when early campaigns produce too little session data." },
        { icon: TCIcons.cursorClick, title: "Low Engagement Data?", desc: "Sparse visitor data makes it harder to compare page versions, offers, and sources." },
        { icon: TCIcons.eyeOff, title: "No Analytics Data?", desc: "You can't optimize what you can't measure. Empty dashboards mean no insights." },
    ];

    const steps = [
        { step: 1, title: "Enter Your URL", desc: "Input your website address and select targeting options: country, device type, and traffic source.", icon: TCIcons.globeSearch },
        { step: 2, title: "We Send Real Signals", desc: "Our residential network delivers traffic through real ISP connections—not data center IPs that get flagged.", icon: TCIcons.signalTower },
        { step: 3, title: "Watch Results Live", desc: "Monitor your traffic in Google Analytics 4 in real-time. See visitors, sessions, and engagement metrics.", icon: TCIcons.monitorChart },
    ];

    return (
        <div className="min-h-screen bg-white text-gray-900 font-sans overflow-x-hidden">
            <SEO
                title="Free Traffic Generator - Get 2,000 Visitors | TC"
                description="Get free website traffic for testing. Receive 2,000 residential visitor sessions with GA4-visible analytics data. No credit card required."
                keywords="free website traffic, free traffic generator, get free traffic, website traffic free, free visitors, free web traffic, boost website traffic free, free SEO traffic, traffic generator free, increase website traffic free"
                schema={[webpageSchema, faqSchema]}
            />

            {/* Sticky Header */}
            <header className={`fixed w-full z-50 transition-all duration-300 ${scrolled ? 'top-0 bg-white/95 backdrop-blur-md shadow-sm border-b border-gray-100' : 'top-0 bg-transparent'}`}>
                <div className="max-w-7xl mx-auto px-6 h-16 md:h-20 flex items-center justify-between">
                    <div className="flex items-center gap-2 cursor-pointer" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
                        <span className="text-xl md:text-2xl font-black text-[#ff5a0a] tracking-tight">TRAFFIC</span>
                        <span className="hidden sm:inline-block text-xs font-bold bg-black text-white px-2 py-1 rounded uppercase tracking-wide">Creator</span>
                    </div>

                    <div className="flex items-center gap-3 md:gap-6">
                        <nav className="hidden md:flex items-center gap-8 text-sm font-bold text-gray-500">
                            <a href="#problem" className="hover:text-[#ff5a0a] transition-colors">Problem</a>
                            <a href="#how-it-works" className="hover:text-[#ff5a0a] transition-colors">How It Works</a>
                            <a href="#pricing" className="hover:text-[#ff5a0a] transition-colors">Pricing</a>
                        </nav>

                        <div className="flex items-center gap-2 md:gap-4">
                            <button
                                onClick={() => handleOpenAuth('login')}
                                className="text-sm font-bold text-gray-600 hover:text-[#ff5a0a] transition-colors px-3 py-2"
                            >
                                Login
                            </button>
                            <button
                                onClick={() => {
                                    trackEvent('cta_click', {
                                        cta_text: 'Sign Up',
                                        cta_location: 'header',
                                        page: location.pathname,
                                    });
                                    handleOpenAuth('signup');
                                }}
                                className="bg-[#ff5a0a] text-white px-4 md:px-6 py-2.5 text-xs md:text-sm font-bold uppercase tracking-wide hover:bg-black transition-colors rounded-xl shadow-lg shadow-orange-500/20"
                            >
                                Start Free →
                            </button>
                        </div>
                    </div>
                </div>
            </header>

            {/* Hero Section */}
            <section className="relative pt-28 md:pt-40 pb-16 md:pb-24 px-6 overflow-hidden">
                <div className="absolute inset-0 z-0">
                    <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-gradient-to-bl from-orange-100/50 to-transparent rounded-full blur-3xl -translate-y-1/2 translate-x-1/3" />
                    <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-gradient-to-tr from-blue-50/50 to-transparent rounded-full blur-3xl translate-y-1/2 -translate-x-1/3" />
                </div>

                <div className="max-w-7xl mx-auto relative z-10">
                    <div className="text-center mb-12 md:mb-16">
                        <div className="inline-flex items-center gap-2 bg-orange-50 border border-orange-100 px-4 py-2 rounded-full text-xs font-bold text-[#ff5a0a] uppercase tracking-wide mb-6">
                            <Star size={14} className="fill-[#ff5a0a]" />
                            100% Free - No Credit Card Required
                        </div>

                        <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black tracking-tight leading-[1.1] mb-6">
                            Free Website Traffic<br />
                            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#ff5a0a] to-orange-500">in Under 5 Minutes</span>
                        </h1>

                        <p className="text-lg md:text-xl text-gray-500 max-w-2xl mx-auto leading-relaxed mb-8">
                            Get <span className="font-bold text-gray-700">6,000 free visitors</span> to your website. Build a traffic baseline and review GA4-visible residential sessions from 190+ countries.
                        </p>
                    </div>

                    <TrafficTestWidget onComplete={handleTestComplete} />
                </div>
            </section>

            {/* Social Proof Bar */}
            <section className="bg-gray-50 border-y border-gray-100 py-8 px-6">
                <div className="max-w-7xl mx-auto">
                    <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                        <div className="flex items-center gap-4 text-sm text-gray-500">
                            <span className="font-bold uppercase tracking-wide text-xs">Compatible with:</span>
                        </div>

                        <div className="flex flex-wrap items-center justify-center gap-8 md:gap-12">
                            {['Google Analytics', 'Semrush', 'WordPress', 'Ahrefs'].map((brand, i) => (
                                <div key={i} className="flex items-center gap-2.5 opacity-60 hover:opacity-100 transition-opacity">
                                    {BrandLogos[brand]}
                                    <span className="text-sm font-bold text-gray-700">{brand}</span>
                                </div>
                            ))}
                        </div>

                        <LiveCounter className="shrink-0 bg-white px-4 py-2 rounded-full border border-gray-200" />
                    </div>
                </div>
            </section>

            {/* === LAZY BLOCK 1: Problem + How It Works + Pricing === */}
            <LazySection minHeight="800px">
            {/* Problem Section */}
            <section id="problem" className="py-20 md:py-32 px-6 bg-gradient-to-b from-gray-50 to-white">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-12 md:mb-16">
                        <div className="inline-flex items-center gap-2 bg-red-50 text-red-600 px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wide mb-4">
                            {TCIcons.alertTriangle}
                            The Problem
                        </div>
                        <h2 className="text-3xl md:text-4xl font-black tracking-tight mb-4">Why Your Website Is Struggling</h2>
                        <p className="text-gray-500 max-w-xl mx-auto">91% of content gets zero traffic from Google. Without visitor signals, search engines ignore your site.</p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-6 md:gap-8">
                        {problems.map((problem, i) => (
                            <div key={i} className="bg-white border border-gray-100 rounded-2xl p-8 hover:shadow-xl hover:border-[#ff5a0a]/30 transition-all group">
                                <div className="w-14 h-14 bg-red-50 rounded-xl flex items-center justify-center mb-6 text-red-500 group-hover:bg-[#ff5a0a] group-hover:text-white transition-colors">
                                    {problem.icon}
                                </div>
                                <h3 className="text-xl font-bold mb-3">{problem.title}</h3>
                                <p className="text-gray-500 text-sm leading-relaxed">{problem.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* How It Works Section */}
            <section id="how-it-works" className="py-20 md:py-32 px-6 bg-white">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-12 md:mb-16">
                        <div className="inline-flex items-center gap-2 bg-[#ff5a0a]/10 text-[#ff5a0a] px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wide mb-4">
                            {TCIcons.zap}
                            How It Works
                        </div>
                        <h2 className="text-3xl md:text-4xl font-black tracking-tight mb-4">Three Simple Steps</h2>
                        <p className="text-gray-500 max-w-xl mx-auto">Start receiving quality traffic in under 5 minutes. No technical knowledge required.</p>
                    </div>

                    <div className="space-y-12 md:space-y-0 md:grid md:grid-cols-3 md:gap-8">
                        {steps.map((step, i) => (
                            <div key={i} className="relative flex flex-col md:flex-col items-start md:items-center text-left md:text-center group">
                                {i < steps.length - 1 && (
                                    <div className="hidden md:block absolute top-12 left-[60%] w-[80%] h-0.5 bg-gradient-to-r from-[#ff5a0a]/30 to-transparent" />
                                )}

                                <div className="flex items-center gap-4 md:flex-col md:gap-0 md:mb-6">
                                    <div className="w-24 h-24 bg-gradient-to-br from-[#ff5a0a]/10 to-orange-50 rounded-2xl flex items-center justify-center text-[#ff5a0a] mb-0 md:mb-4 shrink-0 group-hover:scale-110 transition-transform">
                                        {step.icon}
                                    </div>
                                    <div className="md:hidden">
                                        <div className="text-xs font-bold text-[#ff5a0a] uppercase tracking-wide mb-1">Step {step.step}</div>
                                        <h3 className="text-lg font-bold">{step.title}</h3>
                                        <p className="text-gray-500 text-sm mt-1">{step.desc}</p>
                                    </div>
                                </div>

                                <div className="hidden md:block">
                                    <div className="text-xs font-bold text-[#ff5a0a] uppercase tracking-wide mb-2">Step {step.step}</div>
                                    <h3 className="text-lg font-bold mb-2">{step.title}</h3>
                                    <p className="text-gray-500 text-sm leading-relaxed">{step.desc}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Pricing Section */}
            <section id="pricing" className="py-20 md:py-32 px-6 bg-gray-50">
                <div className="max-w-5xl mx-auto">
                    <div className="text-center mb-12 md:mb-16">
                        <div className="inline-flex items-center gap-2 bg-[#ff5a0a]/10 text-[#ff5a0a] px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wide mb-4">
                            {TCIcons.sparkles}
                            Transparent Pricing
                        </div>
                        <h2 className="text-3xl md:text-4xl font-black tracking-tight mb-4">Pay Only For Results</h2>
                        <p className="text-gray-500 max-w-xl mx-auto">No hidden fees. No monthly contracts. Choose your package and start immediately.</p>
                    </div>

                    <div className="flex justify-center gap-2 mb-8">
                        {TIERS.map((tier) => (
                            <button
                                key={tier.id}
                                onClick={() => {
                                    setSelectedTier(tier.id);
                                    trackEvent('pricing_tier_select', {
                                        tier: tier.id,
                                        page: location.pathname,
                                    });
                                }}
                                className={`px-6 py-3 text-xs font-bold uppercase tracking-widest transition-all ${selectedTier === tier.id
                                    ? 'bg-[#ff5a0a] text-white'
                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                    } ${tier.popular ? 'ring-2 ring-[#ff5a0a]/30' : ''}`}
                            >
                                {tier.name}
                                {tier.popular && <Star size={10} className="ml-2 inline" fill="currentColor" />}
                            </button>
                        ))}
                    </div>

                    <div className="grid md:grid-cols-3 gap-6">
                        {pricingPacks.map((pack, i) => {
                            const price = PRICING_MATRIX[selectedTier][pack.volume]?.[1] || 0;
                            const cpm = getCPM(selectedTier, pack.volume, 1);
                            const tier = TIERS.find(t => t.id === selectedTier);
                            return (
                                <div
                                    key={i}
                                    className={`relative bg-white rounded-2xl p-8 border transition-all ${pack.highlight ? 'border-2 border-[#ff5a0a] shadow-2xl shadow-[#ff5a0a]/20 scale-105 z-10' : 'border-gray-100 hover:border-[#ff5a0a]/30 hover:shadow-xl'}`}
                                >
                                    {pack.badge && (
                                        <div className={`absolute -top-3 left-1/2 -translate-x-1/2 ${pack.badgeColor || 'bg-[#ff5a0a]'} text-white px-4 py-1 text-[10px] font-bold uppercase tracking-widest rounded-full whitespace-nowrap shadow-lg`}>
                                            {pack.badge}
                                        </div>
                                    )}

                                    <div className="mb-6">
                                        <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-2">{pack.name}</h3>
                                        <div className="flex items-baseline gap-1">
                                            <span className="text-4xl font-black text-gray-900">{formatPrice(price)}</span>
                                            <span className="text-gray-400 text-sm font-medium">one-time</span>
                                        </div>
                                    </div>

                                    <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-4 mb-6 text-center border border-gray-100">
                                        <div className="text-2xl font-black text-gray-900">{pack.volume >= 1000000 ? `${pack.volume / 1000000}M` : `${pack.volume / 1000}k`}</div>
                                        <div className="text-xs text-gray-500 font-bold uppercase tracking-wide">Visitors</div>
                                        <div className="text-xs text-[#ff5a0a] font-bold mt-1">~{formatCPM(cpm)}/1k</div>
                                    </div>

                                    <p className="text-sm text-gray-500 mb-6">{pack.desc}</p>

                                    <ul className="space-y-3 mb-8">
                                        {tier?.features.slice(0, 4).map((feature, idx) => (
                                            <li key={idx} className="flex items-center gap-2 text-sm text-gray-600">
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#22C55E" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="shrink-0"><polyline points="20 6 9 17 4 12" /></svg>
                                                {feature}
                                            </li>
                                        ))}
                                    </ul>

                                    <button
                                        onClick={() => {
                                            trackEvent('cta_click', {
                                                cta_text: `Get ${pack.volume >= 1000000 ? `${pack.volume / 1000000}M` : `${pack.volume / 1000}k`} Visitors`,
                                                cta_location: 'pricing_card',
                                                plan_name: pack.name,
                                                plan_volume: pack.volume,
                                                tier: selectedTier,
                                                page: location.pathname,
                                            });
                                            handleOpenAuth('signup');
                                        }}
                                        className={`w-full py-4 rounded-xl text-sm font-bold uppercase tracking-wide transition-all flex items-center justify-center gap-2 ${pack.highlight ? 'bg-[#ff5a0a] text-white hover:bg-black shadow-lg' : 'bg-gray-900 text-white hover:bg-[#ff5a0a]'}`}
                                    >
                                        Get {pack.volume >= 1000000 ? `${pack.volume / 1000000}M` : `${pack.volume / 1000}k`} Visitors <ArrowRight size={16} />
                                    </button>
                                </div>
                            );
                        })}
                    </div>

                    <div className="mt-12 flex flex-wrap justify-center gap-6 text-sm text-gray-500">
                        <div className="flex items-center gap-2">
                            <span className="text-green-500">{TCIcons.checkCircle}</span>
                            Instant delivery
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="text-green-500">{TCIcons.checkCircle}</span>
                            Money-back guarantee
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="text-green-500">{TCIcons.checkCircle}</span>
                            190+ countries
                        </div>
                    </div>
                </div>
            </section>
            </LazySection>

            {/* === LAZY BLOCK 2: Testimonials + Guarantee + FAQ + CTA === */}
            <LazySection minHeight="600px">
            {/* Testimonials Section */}
            <section className="py-20 md:py-32 px-6 bg-white">
                <div className="max-w-7xl mx-auto">
                    <div className="mb-16 text-center">
                        <div className="text-[#ff5a0a] text-xs font-bold uppercase tracking-widest mb-2">Customer Reviews</div>
                        <h2 className="text-3xl md:text-4xl font-black tracking-tight text-gray-900 mb-4">Trusted by 14,000+ Webmasters</h2>
                        <div className="flex items-center justify-center gap-2 mt-4">
                            {[1, 2, 3, 4, 5].map(s => <Star key={s} size={20} className="text-[#ff5a0a] fill-[#ff5a0a]" />)}
                            <span className="text-sm font-bold text-gray-600 ml-2">4.8 / 5.0 Average Rating</span>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {[
                            {
                                name: 'Alex R.',
                                role: 'SEO Consultant, United Kingdom',
                                text: 'Traffic Creator is the only traffic tool I trust. My clients see measurable ranking improvements within 2 weeks. We went from Page 8 to Page 1 for 3 competitive keywords.',
                                metric: 'Page 8 → Page 1',
                                metricLabel: 'Ranking Improvement',
                                rating: 5
                            },
                            {
                                name: 'Sofia M.',
                                role: 'E-commerce Founder, Germany',
                                text: "I was skeptical at first, but the results speak for themselves. Bounce rate on my shop went from 78% to 31% after a single campaign. Support team was brilliant too.",
                                metric: '78% → 31%',
                                metricLabel: 'Bounce Rate Drop',
                                rating: 5
                            },
                            {
                                name: 'James T.',
                                role: 'Agency Owner, United States',
                                text: 'We white-label Traffic Creator for 40+ clients through the API. It is stable, scalable, and the delivery logs give clients hard proof of ROI. Nothing else comes close.',
                                metric: '40+ Clients',
                                metricLabel: 'Managed via API',
                                rating: 5
                            }
                        ].map((review, i) => (
                            <div key={i} className="bg-gray-50 border border-gray-100 p-8 rounded-2xl hover:border-[#ff5a0a]/30 hover:shadow-xl transition-all relative group">
                                <Quote size={32} className="absolute top-6 right-6 text-gray-200 group-hover:text-orange-100 transition-colors" />

                                <div className="bg-gradient-to-br from-orange-50 to-orange-100/50 rounded-xl p-4 mb-6 border border-orange-100">
                                    <div className="text-2xl font-black text-[#ff5a0a]">{review.metric}</div>
                                    <div className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">{review.metricLabel}</div>
                                </div>

                                <div className="flex gap-0.5 mb-4">
                                    {[1, 2, 3, 4, 5].map(s => <Star key={s} size={18} className={`${s <= review.rating ? 'text-[#ff5a0a] fill-[#ff5a0a]' : 'text-gray-300 fill-gray-300'}`} />)}
                                </div>
                                <p className="text-gray-700 text-sm leading-relaxed italic mb-6 relative z-10">"{review.text}"</p>
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-[#ff5a0a] rounded-full flex items-center justify-center text-white font-black text-sm">{review.name.charAt(0)}</div>
                                    <div>
                                        <div className="font-black text-gray-900 text-sm">{review.name}</div>
                                        <div className="text-xs text-gray-400 font-bold uppercase tracking-wide">{review.role}</div>
                                    </div>
                                    <BadgeCheck size={16} className="text-blue-500 ml-auto" />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Guarantee Section */}
            <section className="py-20 md:py-24 px-6 bg-gray-50">
                <div className="max-w-5xl mx-auto">
                    <div className="text-center mb-12">
                        <div className="inline-flex items-center gap-2 bg-green-50 text-green-700 px-4 py-2 rounded-full text-xs font-bold uppercase tracking-widest mb-6">
                            {TCIcons.shieldCheck} Zero-Risk Guarantee
                        </div>
                        <h2 className="text-3xl md:text-4xl font-black tracking-tight mb-4 text-gray-900">
                            Your Investment Is <span className="text-[#ff5a0a]">100% Protected</span>
                        </h2>
                        <p className="text-gray-500 max-w-2xl mx-auto">
                            We're so confident in our platform that we put our money where our mouth is.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-6">
                        {[
                            {
                                icon: TCIcons.shieldGuarantee,
                                title: '72-Hour Delivery Guarantee',
                                desc: 'If we fail to deliver your purchased visitor count within 72 hours, you get a full refund. No questions asked.',
                                highlight: true
                            },
                            {
                                icon: TCIcons.lockKeyhole,
                                title: 'Data Safety Promise',
                                desc: 'Residential IP traffic helps avoid obvious datacenter patterns and keeps campaign reporting easier to review in analytics platforms.'
                            },
                            {
                                icon: TCIcons.receiptCheck,
                                title: 'No Hidden Fees',
                                desc: 'The price you see is the price you pay. No recurring charges, no surprise fees, no mandatory upsells. Ever.'
                            }
                        ].map((item, i) => (
                            <div key={i} className={`relative p-8 rounded-2xl border-2 transition-all hover:shadow-xl ${item.highlight ? 'border-[#ff5a0a] bg-gradient-to-br from-orange-50 to-white shadow-lg' : 'border-gray-200 bg-white hover:border-[#ff5a0a]/30'}`}>
                                {item.highlight && (
                                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#ff5a0a] text-white px-4 py-1 text-[9px] font-bold uppercase tracking-widest rounded-full shadow-lg whitespace-nowrap">
                                        Most Requested
                                    </div>
                                )}
                                <div className={`w-14 h-14 rounded-xl flex items-center justify-center mb-6 ${item.highlight ? 'bg-[#ff5a0a] text-white' : 'bg-green-50 text-green-600'}`}>
                                    {item.icon}
                                </div>
                                <h3 className="text-lg font-black text-gray-900 mb-3">{item.title}</h3>
                                <p className="text-sm text-gray-500 leading-relaxed">{item.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* FAQ Section */}
            <section id="faq" className="py-20 md:py-32 px-6 bg-white">
                <div className="max-w-3xl mx-auto">
                    <div className="text-center mb-12 md:mb-16">
                        <h2 className="text-3xl md:text-4xl font-black tracking-tight mb-4">Frequently Asked Questions</h2>
                        <p className="text-gray-500">Everything you need to know before getting started.</p>
                    </div>

                    <div className="space-y-4">
                        {faqs.map((faq, i) => (
                            <div key={i} className="bg-gray-50 border border-gray-100 rounded-xl overflow-hidden">
                                <button
                                    className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-100 transition-colors"
                                    onClick={() => toggleFaq(i)}
                                >
                                    <span className="font-bold text-gray-900 pr-4">{faq.question}</span>
                                    {openFaq === i ? (
                                        <ChevronUp size={20} className="text-[#ff5a0a] shrink-0" />
                                    ) : (
                                        <ChevronDown size={20} className="text-gray-400 shrink-0" />
                                    )}
                                </button>
                                {openFaq === i && (
                                    <div className="px-6 pb-6 text-gray-600 text-sm leading-relaxed border-t border-gray-100 pt-4">
                                        {faq.answer}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Final CTA Section */}
            <section className="py-20 md:py-32 px-6 bg-[#ff5a0a] relative overflow-hidden">
                <div className="absolute inset-0 opacity-10" />
                <div className="absolute inset-0 bg-gradient-to-br from-orange-600/50 to-transparent" />

                <div className="max-w-4xl mx-auto text-center relative z-10">
                    <h2 className="text-3xl md:text-5xl font-black text-white tracking-tight mb-6">
                        Ready to Boost Your Traffic?
                    </h2>
                    <p className="text-white/80 text-lg mb-8 max-w-xl mx-auto">
                        Join thousands of marketers who trust Traffic Creator for their SEO campaigns.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <button
                            onClick={() => {
                                trackEvent('cta_click', {
                                    cta_text: 'Get 2,000 Free Visitors Now',
                                    cta_location: 'bottom_cta',
                                    page: location.pathname,
                                });
                                handleOpenAuth('signup');
                            }}
                            className="bg-black text-white px-8 py-4 rounded-xl text-sm font-bold uppercase tracking-wide hover:bg-white hover:text-black transition-colors shadow-xl"
                        >
                            Get 2,000 Free Visitors Now <ArrowRight size={18} className="inline ml-2" />
                        </button>
                        <Link
                            to="/#pricing"
                            className="bg-white/10 backdrop-blur-sm border border-white/20 text-white px-8 py-4 rounded-xl text-sm font-bold uppercase tracking-wide hover:bg-white/20 transition-colors"
                        >
                            View All Plans
                        </Link>
                    </div>
                    <div className="mt-8 flex items-center justify-center gap-6 text-white/60 text-sm">
                        <div className="flex items-center gap-2">
                            <Check size={16} /> No credit card
                        </div>
                        <div className="flex items-center gap-2">
                            <Check size={16} /> Cancel anytime
                        </div>
                    </div>
                </div>
            </section>
            </LazySection>

            {/* Footer */}
            <footer className="bg-gray-900 text-white py-12 px-6">
                <div className="max-w-7xl mx-auto">
                    <div className="flex flex-col md:flex-row justify-between items-center gap-8">
                        <div className="flex items-center gap-2">
                            <span className="text-xl font-black text-[#ff5a0a]">TRAFFIC</span>
                            <span className="text-xs font-bold bg-white/10 text-white px-2 py-1 rounded uppercase">Creator</span>
                        </div>

                        <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-400">
                            <Link to="/blog" className="hover:text-white transition-colors">Blog</Link>
                            <Link to="/helpdesk" className="hover:text-white transition-colors">Help Center</Link>
                            <Link to="/legal/privacy" className="hover:text-white transition-colors">Privacy</Link>
                            <Link to="/legal/terms" className="hover:text-white transition-colors">Terms</Link>
                            <Link to="/legal/refund" className="hover:text-white transition-colors">Refund Policy</Link>
                        </div>

                        <div className="text-sm text-gray-500">
                            © {new Date().getFullYear()} Traffic Creator Inc.
                        </div>
                    </div>
                </div>
            </footer>

            {/* Auth Modal */}
            <AuthModal
                isOpen={authModalOpen}
                onClose={() => setAuthModalOpen(false)}
                initialMode={authMode}
                onSuccess={handleAuthSuccess}
                message={authMessage}
            />

            {/* Sticky Mobile CTA */}
            <div className={`fixed bottom-0 left-0 right-0 z-50 md:hidden transition-all duration-300 ${scrolled ? 'translate-y-0' : 'translate-y-full'}`}>
                <div className="bg-white/95 backdrop-blur-md border-t border-gray-200 px-4 py-3 shadow-[0_-4px_20px_rgba(0,0,0,0.1)]">
                    <div className="flex items-center gap-3">
                        <div className="flex-1">
                            <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Limited offer</div>
                            <div className="text-sm font-black text-gray-900">2,000 Free Visitors</div>
                        </div>
                        <button
                            onClick={() => handleOpenAuth('signup')}
                            className="bg-[#ff5a0a] text-white px-6 py-3 text-xs font-bold uppercase tracking-widest rounded-xl shadow-lg shadow-orange-500/30 hover:bg-black transition-colors flex items-center gap-2"
                        >
                            Start Free <ArrowRight size={14} />
                        </button>
                    </div>
                </div>
            </div>

            <ExitIntentPopup onSignup={() => handleOpenAuth('signup')} />
        </div>
    );
};

export default ConversionLandingPage;
